import React, { useState, useEffect } from 'react';
import { useLoader } from '../../../context/LoaderContext';
import { useSearchParams } from 'react-router-dom';
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../common';
import { BILLS_COLUMN, ROUTES, TITLES, BUTTON, LOCAL_STORAGE } from '../../../constants';
import { Button } from '../../shared';
import { vendorBill, printBillPackingList } from '../../../service';
import { useDebounce } from '../../../hooks';
import { billDataCompiler, getLocalStorageItem } from '../../../utils';

export const BillsContent = () => {
  const { setLoading } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [billsData, setBillsData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('filter') || BILLS_COLUMN[10].name);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [isLoading, setIsLoading] = useState(false);
  const [printMode, setPrintMode] = useState(false);
  const [filteredColumn, setFilteredColumn] = useState(BILLS_COLUMN);
  const [billList, setBillList] = useState([]);
  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filter if it's not the default value
    if (params.filter && params.filter !== BILLS_COLUMN[10].name) newParams.filter = params.filter;
    if (params.page) newParams.page = params.page.toString();

    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
    const isFabric = vendorType === 'fabric';
    const isDyeing = vendorType === 'dyeing';
    const isClothing = vendorType === 'clothing';
    let columns = (isFabric || isDyeing || isClothing) && printMode ? BILLS_COLUMN : BILLS_COLUMN.filter((col) => col.name !== '*');
    columns = columns.filter((col) => {
      if (col.name === 'Container No' && (vendorType === 'fabric' || vendorType === 'dyeing')) {
        return false; // Hide Container No if vendor is fabric or dyeing
      }
      return true;
    });
    setFilteredColumn(columns);
  }, [printMode]);

  const fetchBills = useDebounce((params = {}) => {
    setIsLoading(true);
    vendorBill({ data: params })
      .then((response) => {
        setLoading(false);
        if (response?.data?.records?.length > 0) {
          setBillsData(response.data.records);
          setPaginationData(response.data.params);

          // Always update the sorting array with the latest data from the API
          const name = BILLS_COLUMN[10].dataField[0];
          if (response?.data[name] && Array.isArray(response?.data[name])) {
            setSortingArray1(response.data[name]);
          }
        } else {
          setBillsData([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, 400);

  const getBillsList = (additionalParams = {}) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");
    // setBillList([]);
    // setPrintMode(false);

    // Create a sort object based on the current filter
    const sortSelection = {};
    if (sort1 !== BILLS_COLUMN[10].name) {
      sortSelection.state = sort1.toLowerCase();
    }

    const params = {
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
          draft_number: searchTerm,
          invoice_origin: searchTerm,
          'invoice_line_ids.future_lot': searchTerm,
          ref: searchTerm,
          container_no: searchTerm,
          invoice_number: searchTerm,
        }
        : {},
      sort: {
        selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
      },
      ...additionalParams,
    };

    // Update URL params - always include filter in URL if it's not the default value
    updateUrlParams({
      search: searchTerm,
      filter: sort1 !== BILLS_COLUMN[10].name ? sort1 : '',
      page: params.page_number,
    });

    fetchBills(params);
  };

  // Initial load - read from URL params
  useEffect(() => {
    // On initial load, if we have params in the URL, use them
    const urlSearchTerm = searchParams.get('search');
    const urlFilter = searchParams.get('filter');
    const urlPage = searchParams.get('page');

    let hasChanges = false;

    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
      hasChanges = true;
    }

    if (urlFilter && urlFilter !== sort1) {
      setSort1(urlFilter);
      hasChanges = true;
    }

    if (urlPage && parseInt(urlPage) !== pageNumber) {
      setPageNumber(parseInt(urlPage));
      hasChanges = true;
    }

    // Always fetch data on initial load to ensure filters are applied
    if (!hasChanges) {
      getBillsList();
    }
  }, []);

  useEffect(() => {
    if (search) {
      setPageNumber(1); // Reset page number on search
      getBillsList({ page_number: 1 });
    }
  }, [search]);

  useEffect(() => {
    // Only fetch when pageNumber changes and it's not the initial load
    const currentUrlPage = searchParams.get('page');
    if (currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
      getBillsList();
    }
  }, [pageNumber]);

  // Handle sorting changes
  useEffect(() => {
    // Check if sort1 has changed from its initial value
    const initialSort1 = searchParams.get('filter') || BILLS_COLUMN[10].name;

    // Only trigger a new API call if the value has actually changed
    if (sort1 !== initialSort1) {
      setPageNumber(1); // Reset page number on sorting change
      getBillsList({ page_number: 1 });
    }
  }, [sort1]);

  const resetFilter = () => {
    setSort1(BILLS_COLUMN[10].name);
    setPageNumber(1);

    // Remove filter from URL but keep search if present
    updateUrlParams({
      search: searchTerm,
      page: '1',
    });

    getBillsList({ page_number: 1 });
  };

  useEffect(() => {
    if (!searchTerm && search) {
      setPageNumber(1);
      setSearch(false);

      // Update URL params - remove search but keep filter
      updateUrlParams({
        filter: sort1 !== BILLS_COLUMN[10]?.name ? sort1 : '',
        page: '1',
      });

      getBillsList({ page_number: 1 });
    }
  }, [searchTerm]);

  const handleTogglePrint = () => {
    printMode ? (setPrintMode(0), setBillList([]), setSelectionMode("page"), setGlobalSelectAll(false)) : setPrintMode(1);
  }

  const handlePrintPackingList = async () => {
    try {
      let payload;
      if (selectionMode === "all") {
        const sortSelection = {};
        if (sort1 !== BILLS_COLUMN[10].name) {
          sortSelection.state = sort1.toLowerCase();
        }
        payload = {
          select_all: true,
          search: searchTerm
            ? {
              name: searchTerm,
              invoice_origin: searchTerm,
              'invoice_line_ids.future_lot': searchTerm,
              ref: searchTerm,
              container_no: searchTerm,
              invoice_number: searchTerm,
            }
            : {},
          sort: {
            selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
          },
          excluded_ids: Array.from(excludedIds),
        };
      } else {
        payload = {
          select_all: false,
          bill_ids: billList.map((bi) => bi.id),
        };
      }

      const response = await printBillPackingList({ data: payload });

      if (response?.data) {
        window.open(response.data, "_blank");
        setBillList([]);
        setExcludedIds(new Set());
        setSelectionMode("page");
        setPrintMode(false);
        setGlobalSelectAll(false);
      } else {
        console.error("Server Error or Invalid Response: ", response);
      }
    } catch (error) {
      console.error("Server Error: ", error);
    }
  };

  const addBillToList = ({ id }) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id);    // uncheck
      }
      setExcludedIds(next);
    } else {
      if (billList.some((bi) => bi.id === id)) {
        setBillList((prev) => prev.filter((bi) => bi.id !== id));
      } else {
        setBillList((prev) => [{ id }, ...prev]);
      }
    }
  };

  const checked = (id) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id);
    }
    return billList.some((bi) => bi.id === id);
  };

  const handleSelectAllVisible = () => {
    const visibleAssignedIds = billsData.map(row => row.id);
    visibleAssignedIds.forEach(id => {
      if (!checked(id)) {
        addBillToList({ id });
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setBillList([]);
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.BILLS, route: ROUTES.BILLS }]} />

      {/* Search and Filters */}
      <div className="d-flex align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          onDefaultSearch={getBillsList}
        />
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
          {sort1 !== BILLS_COLUMN[10].name && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3"
            >
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>

      {/* Records Selected + Print Button */}
      <div className="d-flex justify-content-between align-items-center mb-3 w-100">
        {/* Left: Buttons + Selected count */}
        <div className="d-flex align-items-center gap-3">
          {(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing') && (
            <>
              <Button
                clickHandler={handleTogglePrint}
                className="blue-btn d-flex align-items-center justify-content-center px-3"
              >
                {printMode ? BUTTON.CANCEL : BUTTON.PRINT_BILL}
              </Button>

              {(printMode && !globalSelectAll) ? (
                <>
                  <Button
                    clickHandler={handleSelectAllVisible}
                    className="blue-btn d-flex align-items-center justify-content-center px-3"
                  >
                    Select Page
                  </Button>
                </>
              ) : ''}
            </>
          )}

          {(billList.length > 0 && !globalSelectAll) && (
            <>
              <span className="text-muted ms-2">
                {billList.length} {billList.length === 1 ? 'record' : 'records'} selected
              </span>
              {!(billList.length === paginationData?.total_records) && <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button"
              >
                Select All ({paginationData?.total_records})
              </span>
              }
            </>
          )}
          {selectionMode === 'all' && (
            <>
              <span className="text-muted ms-2">
                {paginationData?.total_records - excludedIds.size}{' '}
                {(paginationData?.total_records - excludedIds.size) === 1 ? 'record' : 'records'} selected
              </span>
              {!(billList.length === paginationData?.total_records) && excludedIds.size > 0 && <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button"
              >
                Select All ({paginationData?.total_records})
              </span>}
            </>
          )}
        </div>

        {/* Right: Print button */}
        <div className="d-flex">
          {(getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'fabric' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' || getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'clothing') &&
            (billList.length > 0 || selectionMode === 'all') && (
              <Button
                clickHandler={handlePrintPackingList}
                className="blue-btn d-flex align-items-center justify-content-center px-3"
              >
                {BUTTON.PRINT}
              </Button>
            )}
        </div>
      </div>


      {/* Table */}
      {Array.isArray(billsData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={billsData}
          colFilter={false}
          to={printMode ? '' : ROUTES.BILLS}
          compiler={{ use: true, tool: billDataCompiler }}
          func={{ addBillToList, checked }}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
    </>
  );
};